/**
 * MFX OS — Job Tickets (blueprint Stage 6). OS-chain tickets only; the
 * legacy OS job tracker keeps its own view of the same collection.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOsList, fmtDate } from '../../os/core';
import { Chip } from '../../os/ui';
import type { JobTicketOS } from '../../os/types';

export default function Tickets() {
  const nav = useNavigate();
  const { rows, loading } = useOsList<JobTicketOS>('jobTickets', { wheres: [['source', '==', 'mfx-os']], order: ['createdAt', 'desc'] });
  const [q, setQ] = useState('');

  const shown = useMemo(() => {
    if (!q.trim()) return rows;
    const t = q.toLowerCase();
    return rows.filter((x) => `${x.displayId} ${x.customerName} ${x.soNum}`.toLowerCase().includes(t));
  }, [rows, q]);

  const blocked = rows.filter((x) => (x.blockedCount || 0) > 0).length;
  const ready = rows.filter((x) => x.readinessPct === 100).length;

  if (loading) return <div className="loading"><div className="spinner" /> Loading tickets…</div>;

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-title">Job Tickets</div>
          <div className="page-sub">Execution hubs — one per sales order, production jobs + green-light gates inside.</div>
        </div>
      </div>

      <div className="grid cols-3" style={{ marginBottom: 16 }}>
        <div className="metric-card"><div className="metric-label">Open tickets</div><div className="metric-value" style={{ color: 'var(--ac)' }}>{rows.length}</div><div className="metric-note">OS chain</div></div>
        <div className="metric-card"><div className="metric-label">With blockers</div><div className="metric-value" style={{ color: 'var(--rd)' }}>{blocked}</div><div className="metric-note">gate(s) blocked</div></div>
        <div className="metric-card"><div className="metric-label">Fully ready</div><div className="metric-value" style={{ color: 'var(--gn)' }}>{ready}</div><div className="metric-note">100% green lights</div></div>
      </div>

      <div className="toolbar"><input className="search" placeholder="Search ticket, client, SO…" value={q} onChange={(e) => setQ(e.target.value)} /></div>

      <div className="table-wrap"><table>
        <thead><tr><th>Ticket</th><th>Client</th><th>Sales order</th><th>Jobs</th><th>Due</th><th>Readiness</th><th>Blocked</th><th>Status</th></tr></thead>
        <tbody>
          {shown.map((x) => (
            <tr key={x.id} onClick={() => nav(`/os/ticket?id=${encodeURIComponent(x.id)}`)}>
              <td><strong>{x.displayId}</strong></td>
              <td>{x.customerName}</td>
              <td>{x.soNum}</td>
              <td>{x.productionJobIds?.length || 0}</td>
              <td>{fmtDate(x.dueDate)}</td>
              <td>
                <div className="os-meter"><div className="os-meter-fill" style={{ width: `${x.readinessPct || 0}%`, background: (x.readinessPct || 0) === 100 ? 'var(--gn)' : 'var(--yl)' }} /></div>
                <span style={{ fontSize: 11, color: 'var(--tx2)' }}>{x.readinessPct || 0}%</span>
              </td>
              <td style={{ color: x.blockedCount ? 'var(--rd)' : 'var(--tx3)' }}>{x.blockedCount || 0}</td>
              <td><Chip label={x.status} /></td>
            </tr>
          ))}
          {!shown.length && <tr><td colSpan={8}><div className="empty">No OS-chain tickets yet — create one from a sales order.</div></td></tr>}
        </tbody>
      </table></div>
    </>
  );
}
